import { jsPDF } from 'jspdf';

/**
 * Sanitize text for jsPDF - removes problematic Unicode that causes
 * character spacing/corruption (e.g. zero-width chars, smart quotes).
 * Helvetica has limited Unicode support.
 */
function sanitizeTextForPDF(text: string): string {
  if (!text || typeof text !== 'string') return '';
  return text
    .replace(/[\u200B-\u200D\uFEFF\u00AD]/g, '') // Zero-width, soft hyphen
    .replace(/[\u2018\u2019]/g, "'")              // Smart quotes
    .replace(/[\u201C\u201D]/g, '"')             // Smart double quotes
    .replace(/\u2014/g, ' - ')                   // Em dash
    .replace(/\u2013/g, '-')                     // En dash
    .replace(/\u2022/g, '-')                     // Bullet
    .replace(/\u2192/g, '->')                      // Right arrow (common in recommendations)
    .replace(/\r\n/g, '\n')                       // Normalize line endings
    .replace(/\r/g, '\n')
    .replace(/ +/g, ' ')                          // Collapse multiple spaces (not newlines)
    .trim();
}

export interface GeneratePDFOptions {
  title: string;
  content: string;
  country?: string;
  date?: string;
  /** Kept for compatibility; branding output is intentionally disabled. */
  showBranding?: boolean;
}

export const generatePDF = ({
  title,
  content,
  country,
  date,
  showBranding = false,
}: GeneratePDFOptions): Blob => {
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4'
  });

  // Configuration
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxLineWidth = pageWidth - (margin * 2);
  let yPosition = margin;

  // Helper to add new page if needed
  const checkAndAddPage = (increment: number) => {
    if (yPosition + increment > pageHeight - margin) {
      doc.addPage();
      yPosition = margin;
    }
  };

  // Branding intentionally disabled for all plans to avoid user-facing friction.
  void showBranding;

  // Title
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(0, 0, 0);
  const titleLines = doc.splitTextToSize(sanitizeTextForPDF(title), maxLineWidth);
  titleLines.forEach((line: string) => {
    checkAndAddPage(8);
    doc.text(line, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 8;
  });
  yPosition += 5;

  // Metadata
  if (country || date) {
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    let metadata = '';
    if (country) metadata += `País: ${country}`;
    if (country && date) metadata += ' | ';
    if (date) metadata += `Fecha: ${date}`;
    doc.text(metadata, pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 10;
  }

  // Line separator
  doc.setDrawColor(200, 200, 200);
  doc.line(margin, yPosition, pageWidth - margin, yPosition);
  yPosition += 10;

  // Content
  doc.setFontSize(11);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(0, 0, 0);

  const sanitizedContent = sanitizeTextForPDF(content);
  const contentLines = sanitizedContent.split('\n');
  contentLines.forEach((line) => {
    if (!line.trim()) {
      yPosition += 5;
      return;
    }

    const wrappedLines = doc.splitTextToSize(line, maxLineWidth);
    wrappedLines.forEach((wrappedLine: string) => {
      checkAndAddPage(7);
      doc.text(wrappedLine, margin, yPosition);
      yPosition += 7;
    });
  });

  // No branding footer.

  // Return as Blob
  return doc.output('blob');
};

export const downloadPDF = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename.endsWith('.pdf') ? filename : `${filename}.pdf`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

/** Analysis result structure for PDF report */
interface AnalysisReportInput {
  document_name: string;
  document_type: string;
  country: string;
  created_at: string;
  analysis_result: {
    overall_risk: string;
    summary: string;
    risks: Array<{ severity: string; description: string; recommendation?: string }>;
    omissions: Array<{ description: string; recommendation: string }>;
    recommendations: string[];
  };
}

/** Translations/labels for the report (to avoid i18n dependency in core) */
export interface AnalysisReportLabels {
  reportTitle: string;
  type: string;
  country: string;
  date: string;
  overallRisk: string;
  riskLevel: (level: string) => string;
  summary: string;
  risks: string;
  omissions: string;
  recommendations: string;
}

const LINE_HEIGHT_RATIO = 0.5;  // Line height = fontSize * this (in mm)

export function generateAnalysisReportPDF(
  data: AnalysisReportInput,
  labels: AnalysisReportLabels,
  locale: { code: string }
): Blob {
  const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 20;
  const maxLineWidth = pageWidth - 2 * margin;
  let yPos = margin;

  const addText = (text: string, fontSize: number, isBold: boolean) => {
    const clean = sanitizeTextForPDF(text);
    if (!clean) return;
    doc.setFontSize(fontSize);
    doc.setFont('helvetica', isBold ? 'bold' : 'normal');
    doc.setTextColor(0, 0, 0);
    const lineHeight = fontSize * LINE_HEIGHT_RATIO + 1;
    const lines = doc.splitTextToSize(clean, maxLineWidth);
    for (const line of lines) {
      if (yPos > pageHeight - margin - 15) {
        doc.addPage();
        yPos = margin;
      }
      doc.text(line, margin, yPos);
      yPos += lineHeight;
    }
    yPos += 4;
  };

  // Title
  addText(labels.reportTitle, 18, true);
  addText(data.document_name, 12, false);
  yPos += 6;

  // Metadata
  addText(`${labels.type}: ${data.document_type}`, 10, false);
  addText(`${labels.country}: ${data.country}`, 10, false);
  addText(`${labels.date}: ${formatDateForPDF(data.created_at, locale.code)}`, 10, false);
  yPos += 10;

  // Overall Risk
  addText(labels.overallRisk, 14, true);
  addText(labels.riskLevel(data.analysis_result.overall_risk), 12, false);
  yPos += 10;

  // Summary
  addText(labels.summary, 14, true);
  addText(data.analysis_result.summary, 10, false);
  yPos += 10;

  // Risks
  if (data.analysis_result.risks?.length > 0) {
    addText(`${labels.risks} (${data.analysis_result.risks.length})`, 14, true);
    data.analysis_result.risks.forEach((r, i) => {
      addText(`${i + 1}. [${(r.severity || '').toUpperCase()}] ${r.description || ''}`, 10, false);
      if (r.recommendation) addText(`   -> ${r.recommendation}`, 9, false);
    });
    yPos += 6;
  }

  // Omissions
  if (data.analysis_result.omissions?.length > 0) {
    addText(`${labels.omissions} (${data.analysis_result.omissions.length})`, 14, true);
    data.analysis_result.omissions.forEach((o, i) => {
      addText(`${i + 1}. ${o.description || ''}`, 10, false);
      addText(`   -> ${o.recommendation || ''}`, 9, false);
    });
    yPos += 6;
  }

  // Recommendations
  if (data.analysis_result.recommendations?.length > 0) {
    addText(`${labels.recommendations} (${data.analysis_result.recommendations.length})`, 14, true);
    data.analysis_result.recommendations.forEach((rec, i) => {
      addText(`${i + 1}. ${rec}`, 10, false);
    });
  }

  return doc.output('blob');
}

function formatDateForPDF(isoDate: string, localeCode: string): string {
  try {
    const d = new Date(isoDate);
    return d.toLocaleDateString(localeCode === 'es' ? 'es-ES' : 'en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });
  } catch {
    return isoDate;
  }
}

